import { AxiosPromise } from 'axios';

import { axios } from 'src/app/utils';
import { urlsConfig } from 'src/configs';
import { queryTypes, serviceProjectInputTypes } from 'src/types';
import { fileHelper, urlHelper } from '../../helpers';

export const lookupProjectInputTable = ({
  limit,
  offset,
  ids,
  value,
  order,
  sortOrder,
  blockSpinner,
  load,
}: serviceProjectInputTypes.ProjectInputLookupDataType): AxiosPromise<serviceProjectInputTypes.ProjectInputTableLookupResultType> => {
  const query: queryTypes.LookupQueryParametersType = {};

  if (ids) {
    query.ids = ids.join(',');
  }

  if (value) {
    return axios.get(urlHelper.addQueryParameters({ search: value }, urlsConfig.projectInput.lookupProjectInput), {
      blockSpinner,
    });
  }

  if (order && sortOrder) {
    query.order = order;
    query.sortOrder = sortOrder;
  }

  if (limit && offset) {
    query.limit = limit;
    query.offset = offset;
  }

  return axios.get(urlHelper.addQueryParameters(query, urlsConfig.projectInput.lookupProjectInput), {
    blockSpinner,
  });
};

export const lookupProjectInputWithEvent = ({
  limit,
  offset,
  ids,
  value,
  order,
  eventIds,
  sortOrder,
  blockSpinner,
  load,
}: serviceProjectInputTypes.ProjectInputLookupDataType): AxiosPromise<serviceProjectInputTypes.ProjectInputTableLookupResultType> => {
  const query: queryTypes.LookupQueryParametersType = {};

  if (ids) {
    query.ids = ids.join(',');
  }

  if (value) {
    query.search = value;
  }

  if (eventIds) {
    return axios.get(`${urlsConfig.projectInput.lookupProjectInput}?eventIds=${eventIds}`, {
      blockErrorHandler: true,
    });
  }

  if (order && sortOrder) {
    query.order = order;
    query.sortOrder = sortOrder;
  }

  if (limit && offset) {
    query.limit = limit;
    query.offset = offset;
  }

  return axios.get(urlHelper.addQueryParameters(query, urlsConfig.projectInput.lookupProjectInput), {
    blockSpinner,
  });
};

export const lookupProjectInputOfOntology = ({
  limit,
  offset,
  ids,
  value,
  order,
  sortOrder,
  blockSpinner,
  load,
}: serviceProjectInputTypes.ProjectInputLookupDataType): AxiosPromise<serviceProjectInputTypes.ProjectInputOfOntologyLookupResultType> => {
  const query: queryTypes.LookupQueryParametersType = {};

  if (ids) {
    query.ids = ids.join(',');
  }

  if (value) {
    query.search = value;
  }

  if (order && sortOrder) {
    query.order = order;
    query.sortOrder = sortOrder;
  }

  if (limit && offset) {
    query.limit = limit;
    query.offset = offset;
  }

  return axios.get(urlHelper.addQueryParameters(query, urlsConfig.projectInput.lookupProjectInputOntology), {
    blockSpinner,
  });
};

export const createProjectInput = (
  form: serviceProjectInputTypes.ProjectInputCreateDataType,
  // formData: FormData,
  options: { blockErrorHandler: boolean },
): AxiosPromise<serviceProjectInputTypes.ProjectInputCreateResultType> => {
  const entitiesData = fileHelper.convertArrayData(form.inputtedAudienceEntities);
  const formData = fileHelper.convertDataURLsToFormData(form.attachments);

  if (form.eventId) {
    formData.append('eventId', form.eventId);
  }

  if (form.taskNumberId) {
    formData.append('taskNumberId', form.taskNumberId);
  }

  if (form.projectInputNumber) {
    formData.append('projectInputNumber', form.projectInputNumber);
  }

  if (form.projectInputName) {
    formData.append('projectInputName', form.projectInputName);
  }

  if (form.scriptedById) {
    formData.append('scriptedById', form.scriptedById);
  }

  if (form.scriptedDateTime) {
    formData.append('scriptedDateTime', form.scriptedDateTime.toString());
  }

  if (form.modifiedById) {
    formData.append('modifiedById', form.modifiedById);
  }

  if (form.modifiedDateTime) {
    formData.append('modifiedDateTime', form.modifiedDateTime.toString());
  }

  if (form.inputterId) {
    formData.append('inputterId', form.inputterId);
  }

  if (form.projectInputDirection) {
    formData.append('projectInputDirection', form.projectInputDirection);
  }

  if (form.inputtedAudienceEntities) {
    form.inputtedAudienceEntities.forEach((e) => formData.append('projectInputAudienceEntities', e as any as string));
  }

  if (form.injectionType) {
    formData.append('injectionType', form.injectionType);
  }

  if (form.dateTimeGroup) {
    formData.append('dateTimeGroup', form.dateTimeGroup.toString());
  }
  if (form.timeDetails) {
    formData.append('timeDetails', JSON.stringify(form.timeDetails));
  }

  if (form.type) {
    formData.append('type', form.type.toString());
  }

  if (form.projectInputReferenceId) {
    formData.append('projectInputReferenceId', form.projectInputReferenceId);
  }

  if (form.kanbanReferenceId) {
    formData.append('kanbanReferenceId', form.kanbanReferenceId);
  }

  if (form.projectInputMeanId) {
    formData.append('projectInputMeanId', form.projectInputMeanId);
  }

  if (form.description) {
    formData.append('description', form.description);
  }

  if (form.coordinationRemarks) {
    formData.append('coordinationRemarks', form.coordinationRemarks);
  } else {
    formData.append('coordinationRemarks', '');
  }

  if (form.functionalAreaMessage) {
    formData.append('functionalAreaMessage', form.functionalAreaMessage);
  }

  if (form.log) {
    formData.append('log', form.log);
  }

  if (form.comment) {
    formData.append('comment', form.comment);
  } else {
    formData.append('comment', '');
  }

  if (form.projectInputStatusId) {
    formData.append('projectInputStatusId', form.projectInputStatusId);
  }
  formData.append('isAnchor', form.isAnchor.toString());

  return axios.post(urlsConfig.projectInput.createProjectInput, formData, { blockErrorHandler: true });
};

export const projectInputEdit = (
  form: serviceProjectInputTypes.ProjectInputEditDataType,
  // formData: FormData,
): AxiosPromise<serviceProjectInputTypes.ProjectInputEditResultType> => {
  const entitiesData = fileHelper.convertArrayData(form.inputtedAudienceEntities);

  const formData = fileHelper.convertDataURLsToFormData(form.attachments);

  if (form.id !== undefined) {
    formData.append('id', form.id);
  }

  if (form.deletedAttachments !== undefined) {
    form.deletedAttachments.forEach((eachAttachment: any) => {
      formData.append('deletedAttachments', eachAttachment);
    });
  }

  if (form.eventId) {
    formData.append('eventId', form.eventId);
  }

  if (form.taskNumberId) {
    formData.append('taskNumberId', form.taskNumberId);
  }

  if (form.projectInputNumber) {
    formData.append('projectInputNumber', form.projectInputNumber);
  }

  if (form.projectInputName) {
    formData.append('projectInputName', form.projectInputName);
  }

  if (form.scriptedById) {
    formData.append('scriptedById', form.scriptedById);
  }

  if (form.scriptedDateTime) {
    formData.append('scriptedDateTime', form.scriptedDateTime.toString());
  }

  if (form.modifiedById) {
    formData.append('modifiedById', form.modifiedById);
  }

  if (form.modifiedDateTime) {
    formData.append('modifiedDateTime', form.modifiedDateTime.toString());
  }

  if (form.inputterId) {
    formData.append('inputterId', form.inputterId);
  }

  if (form.projectInputDirection) {
    formData.append('projectInputDirection', form.projectInputDirection);
  }

  if (form.inputtedAudienceEntities) {
    form.inputtedAudienceEntities.forEach((e) => formData.append('projectInputAudienceEntities', e as any as string));
  }

  if (form.injectionType) {
    formData.append('injectionType', form.injectionType);
  }

  if (form.dateTimeGroup) {
    formData.append('dateTimeGroup', form.dateTimeGroup.toString());
  }
  // else {
  //   formData.append('dateTimeGroup', '0');
  // }

  if (form.timeDetails) {
    formData.append('timeDetails', JSON.stringify(form.timeDetails));
  }

  if (form.type) {
    formData.append('type', form.type.toString());
  }

  if (form.projectInputReferenceId) {
    formData.append('projectInputReferenceId', form.projectInputReferenceId);
  }

  if (form.kanbanReferenceId) {
    formData.append('kanbanReferenceId', form.kanbanReferenceId);
  }

  if (form.projectInputMeanId) {
    formData.append('projectInputMeanId', form.projectInputMeanId);
  }

  if (form.description) {
    formData.append('description', form.description);
  } else {
    formData.append('description', '');
  }

  if (form.coordinationRemarks) {
    formData.append('coordinationRemarks', form.coordinationRemarks);
  } else {
    formData.append('coordinationRemarks', '');
  }

  if (form.functionalAreaMessage) {
    formData.append('functionalAreaMessage', form.functionalAreaMessage);
  }

  if (form.log) {
    formData.append('log', form.log);
  }

  if (form.comment) {
    formData.append('comment', form.comment);
  } else {
    formData.append('comment', '');
  }

  if (form.projectInputStatusId) {
    formData.append('projectInputStatusId', form.projectInputStatusId);
  }

  formData.append('isAnchor', form.isAnchor.toString());

  return axios.patch(urlsConfig.projectInput.projectInputEdit, formData, { blockErrorHandler: true });
};

export const projectInputDelete = ({
  id,
}: serviceProjectInputTypes.ProjectInputDeleteDataType): AxiosPromise<serviceProjectInputTypes.ProjectInputDeleteResultType> => {
  return axios.delete(`${urlsConfig.projectInput.projectInputDelete}/${id}`);
};
